import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import jwtDecode from 'jwt-decode';
import { NgxPermissionsService } from 'ngx-permissions';
import { environment } from 'environments/environment';
import { User } from '@auth0/auth0-spa-js';
import { BehaviorSubject } from 'rxjs';
import { CallCenterModeService } from '@services/call-center/call-center-mode.service';
import { ResellerModeService } from '@services/reseller/reseller-mode.service';

interface TenantClaims {
  [tenantId: string]: {
    permissions: string[];
    roles: string[];
  };
}

export enum permission {
  'api-key:write' = 'api-key:write',
  'api-key:read' = 'api-key:read',
  'audit:read' = 'audit:read',
  'auth-config:read' = 'auth-config:read',
  'auth-config:write' = 'auth-config:write',
  'boss.entity.create:write' = 'boss.entity.create:write',
  'boss.entity:read' = 'boss.entity:read',
  'boss.entity.edit:write' = 'boss.entity.edit:write',
  'boss.entity.assign:write' = 'boss.entity.assign:write',
  'boss.entity.pricing:write' = 'boss.entity.pricing:write',
  'boss.entity.availability:write' = 'boss.entity.availability:write',
  'boss.entity:delete' = 'boss.entity:delete',
  'catalog.product-image:write' = 'catalog.product-image:write',
  'catalog:write' = 'catalog:write',
  'catalog:read' = 'catalog:read',
  'coupon-management:read' = 'coupon-management:read',
  'coupon-management:write' = 'coupon-management:write',
  'menu:read' = 'menu:read',
  'menu:write' = 'menu:write',
  'order:read' = 'order:read',
  'order.refunds:write' = 'order.refunds:write',
  'pos.certificate:read' = 'pos.certificate:read',
  'payment-config.template:read' = 'payment-config.template:read',
  'quantity-limit:read' = 'quantity-limit:read',
  'quantity-limit:write' = 'quantity-limit:write',
  'out-of-stock:write' = 'out-of-stock:write',
  'payment-config.template:write' = 'payment-config.template:write',
  'report:read' = 'report:read',
  'store:read' = 'store:read',
  'store.bulk-updates:write' = 'store.bulk-updates:write',
  'store.bulk-updates:read' = 'store.bulk-updates:read',
  'store.enable:write' = 'store.enable:write',
  'payment-config:write' = 'payment-config:write',
  'payment-config:read' = 'payment-config:read',
  'payment-config.store:read' = 'payment-config.store:read',
  'store.hours:write' = 'store.hours:write',
  'store-images:read' = 'store-images:read',
  'store-images:write' = 'store-images:write',
  'store.pause-orders:write' = 'store.pause-orders:write',
  'store.pause-orders:read' = 'store.pause-orders:read',
  'store.prep-time:write' = 'store.prep-time:write',
  'store.provisioning:write' = 'store.provisioning:write',
  'store.catalog:write' = 'store.catalog:write',
  'store:write' = 'store:write',
  'tenant:read' = 'tenant:read',
  'tenant:write' = 'tenant:write',
  'txp-configs:read' = 'txp-configs:read',
  'user:read' = 'user:read',
  'user:write' = 'user:write',
  'store.scheduled-orders:write' = 'store.scheduled-orders:write',
  'store.scheduled-orders:read' = 'store.scheduled-orders:read',
  'ordering-templates:read' = 'ordering-templates:read',
  'ordering-templates:write' = 'ordering-templates:write',
  'call-centers:read' = 'call-centers:read',
  'call-centers:write' = 'call-centers:write',
  'call-dispositions:read' = 'call-dispositions:read',
  'call-dispositions:write' = 'call-dispositions:write',
  'customer-support-interactions:read' = 'customer-support-interactions:read',
  'customer-support-interactions:write' = 'customer-support-interactions:write',
  'customer-support-scripts:read' = 'customer-support-scripts:read',
  'customer-support-scripts:write' = 'customer-support-scripts:write',
  'dnis-mappings:read' = 'dnis-mappings:read',
  'dnis-mappings:write' = 'dnis-mappings:write',
  'role:read' = 'role:read',
  'role:write' = 'role:write',
  'reseller:read' = 'reseller:read',
  'reseller:write' = 'reseller:write',
  'reseller.tenant:read' = 'reseller.tenant:read',
  'store-group:read' = 'store-group:read',
  'store-group:write' = 'store-group:write',
  'store.settings:write' = 'store.settings:write',
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  loaded$ = new BehaviorSubject(false);
  permissionsBackup: string[] = [];
  tenantIds: string[] = [];
  userProfile: User;

  constructor(
    private authService: AuthService,
    private ngxPermissionsService: NgxPermissionsService,
    private callCenterModeService: CallCenterModeService,
    private resellerModeService: ResellerModeService,
  ) {
    this.authService
      .getAccessTokenSilently({
        authorizationParams: {
          active_tenant_identifier: localStorage.getItem('active_tenant_identifier'),
          source: 'permissions',
        },
      })
      .subscribe({
        next: (jwt) => {
          this.setPermissionsFromJwt(jwt);
          this.loaded$.next(true);
        },
        error: (err) => {
          console.error('[PermissionsService] getAccessTokenSilently error:', err);
          // Still mark as loaded so role-routing can detect the empty tenantIds
          // state and show a graceful error rather than spinning forever.
          this.loaded$.next(true);
        },
      });

    this.authService.user$.subscribe((user) => {
      this.userProfile = user ? user : {};
    });
  }

  setPermissionsFromJwt(jwt: string) {
    // Decode the full JWT
    const decodedJwt = jwtDecode(jwt);

    // Extract TCE namespace
    const tceNamespace = decodedJwt['https://tce.tillster.com/'] || {};

    // Log tenant IDs array (tids)
    const tids = tceNamespace.tids || [];
    if (tids.length === 0) {
      console.warn(
        '[PermissionsService] WARNING: tids is empty — user has no tenant IDs in the JWT. This account may have no Brands assigned.',
      );
    }

    // Log tenant claims
    const { tenantClaims } = tceNamespace;

    // If no tenant claims, we can't set permissions yet (multi-tenant user scenario)
    if (!tenantClaims) {
      console.warn(
        '[PermissionsService] WARNING: tenantClaims is missing from JWT — no permissions can be set.',
      );
      // Store tenant IDs from tids array
      this.tenantIds = [...tids];
      return;
    }

    // Check if tenantClaims exists but has empty permissions (multi-tenant user before selection)
    const hasEmptyPermissions = Object.values(tenantClaims as TenantClaims).every(
      (claim) => claim.permissions.length === 0 && claim.roles.length === 0,
    );

    if (hasEmptyPermissions) {
      console.warn(
        '[PermissionsService] WARNING: tenantClaims exists but all permissions and roles are empty — user may have no Brands assigned.',
      );
      // Store tenant IDs from tids array
      this.tenantIds = [...tids];
      return;
    }

    const { '*': global, ...tenants } = tenantClaims as TenantClaims;
    const permissions = [];

    // Clear previous tenant IDs before setting new ones
    this.tenantIds = [];

    if (global) {
      permissions.push(...global.permissions, ...global.roles);
      this.tenantIds.push('*');
    }

    Object.entries(tenants).forEach(([tenantId, claim]) => {
      this.tenantIds.push(tenantId);
      permissions.push(
        ...claim.permissions.map((p) => `${tenantId}:${p}`),
        ...claim.roles.map((role) => `${tenantId}:${role}`),
      );
    });

    this.permissionsBackup = permissions;
    console.log('[PermissionsService] Permissions loaded:', {
      tenantIds: this.tenantIds,
      permissions,
    });
    this.ngxPermissionsService.addPermission(permissions);
    if (this.getRole('CallCenterAgent')) {
      this.callCenterModeService.setMode(true);
    }

    // Set reseller availability for users with reseller permission
    const resellerAvailable = this.getPermission('reseller:read');
    this.resellerModeService.setAvailable(resellerAvailable);
  }

  getPermission(name: keyof typeof permission) {
    const brandId = sessionStorage.getItem('brand_id');
    // First check the standard way (with brandId or global)
    const hasPermission = !!(
      !name ||
      this.ngxPermissionsService.getPermission('SuperAdmin') ||
      this.ngxPermissionsService.getPermission(name) ||
      this.ngxPermissionsService.getPermission(`${brandId}:*`) ||
      this.ngxPermissionsService.getPermission(`${brandId}:${name}`)
    );

    // If not found and no brandId, check permissionsBackup as fallback
    // This handles permissions like tenant:write that are tenant-scoped
    // but need to be checked when brandId is not set (e.g., creating new brand)
    if (!hasPermission && !brandId) {
      return this.permissionsBackup.some((permission) => permission.includes(name));
    }

    return hasPermission;
  }

  getRole(name: string) {
    const brandId = sessionStorage.getItem('brand_id');
    // First check the standard way (with brandId or global)
    const hasRole = !!(
      !name ||
      this.ngxPermissionsService.getPermission(name) ||
      this.ngxPermissionsService.getPermission(`${brandId}:${name}`)
    );

    // If not found and no brandId, check permissionsBackup as fallback
    // This handles roles like ResellerProductOperations that are tenant-scoped
    // but need to be checked when brandId is not set
    if (!hasRole && !brandId) {
      return this.permissionsBackup.some((permission) => permission.includes(name));
    }

    return hasRole;
  }

  deleteCatalogItemPermission(): boolean {
    return environment.canDeleteItem.includes(this.userProfile.email);
  }
}
